import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import { z } from "zod";
import { isNull } from "lodash";
import { useEffectOnce } from "react-use";
import { actions, selectors } from "rdx";
import projectMessages from "rdx/modules/projects/messages";
import adminMessages from "rdx/modules/adminProjects/messages";
import SmartForm from "components/SmartForm";
import { T3PartnerT } from "components/Drawers/SelectT3PartnerDrawer/types";
import { useSmartForm } from "hooks/useSmartForm";
import usePermissions, { permissionTypes } from "hooks/usePermissions";
import useRouteMatcher from "hooks/useRouteMatcher";
import { canSendVision } from "util/canSendVision";
import { convertToBool } from "lib/helpers";
import { productsForHomeownerInterest, type ProjectProductT } from "lib/misc/projectProducts";
import { proposalProductOptions } from "lib/misc/homeownerInterestOptions";
import { SegmentedControl } from "components/mui-styled-components/SmartFormElements/SmartFormSegmentedControl";
import { FundingFinanceOptionsT } from "types/Project";
import { SmartFormDropdown, SmartFormTextarea } from "components/mui-styled-components/SmartFormElements";
import DesignToolSelector, { DesignToolT } from "components/mui-styled-components/DesignToolSelector";
import { L2pTabs } from "../../index";
import MissingSiteInfoBanner from "./MissingSiteInfoBanner";
import { BatteryOnlyNotes, DesignTeamNotes, SelfDesignNotes } from "./VisionDesignNotes";
import CannotRequestProposal from "./CannotRequestProposal";

import * as S from "./styles";

const schema = z
  .object({
    proposalType: z.string().min(1, { message: "A proposal type is required." }),
    visionSelfDesign: z.boolean(),
    requestedTurnaround: z.string(),
    proposalNotes: z.string(),
    designTool: z.enum(["vision", "aurora"]),
    // Not a rendered field. Mirrors "this request has no design step" into form state so the
    // refine below can see it -- Battery-Only has no turnaround to require.
    skipsDesign: z.boolean(),
  })
  .refine(
    (data) =>
      data.skipsDesign ||
      (convertToBool(data.visionSelfDesign) && data.proposalType === "vision") ||
      !!data.requestedTurnaround,
    {
      message: "A requested turnaround time is required.",
      path: ["requestedTurnaround"],
    },
  );

const blankFormData = {
  proposalType: "",
  visionSelfDesign: false,
  requestedTurnaround: "",
  proposalNotes: "",
  designTool: "vision" as DesignToolT,
  skipsDesign: false,
};

export type ProposalSchemaT = z.infer<typeof schema>;

type AuroraFinanceOptionsT = Pick<FundingFinanceOptionsT, "financeProducts">;

type ProposalRequestFormPropsT = {
  reference: React.LegacyRef<HTMLButtonElement>;
  latestMessage: { message: string; type: string };
  rsmRequirementsPassed: boolean;
  auroraFinanceOptions: AuroraFinanceOptionsT;
  assignedPartner?: T3PartnerT;
  setIsVisionSelfDesign: (bool: boolean) => void;
  setIsProposalFormValid: (bool: boolean) => void;
  setIsVisionRequestSubmitted: (bool: boolean) => void;
  setSubmittedDesignTool: (designTool: DesignToolT) => void;
  checkForReqdSiteInfo: () => { isQualifyingSiteInfoPresent: boolean };
  isReqdSiteInfoMissing: boolean;
  setCurrentTab: (tabIdx: number) => void;
};

type VisionRequestTypeT = "self-request" | "overnight" | "real-time";

const ProposalRequestForm = ({
  latestMessage,
  reference,
  rsmRequirementsPassed,
  auroraFinanceOptions,
  assignedPartner,
  setIsVisionSelfDesign,
  setIsProposalFormValid,
  setIsVisionRequestSubmitted,
  setSubmittedDesignTool,
  checkForReqdSiteInfo,
  isReqdSiteInfoMissing,
  setCurrentTab,
}: ProposalRequestFormPropsT) => {
  const dispatch = useDispatch();

  const currentUser = useSelector(selectors.getCurrentUser);
  const projectId = useSelector(selectors.selectLeadToProposalDrawerProjectId);
  const projectDetails = useSelector(selectors.selectLeadToProposalProjectDetails);
  const leadDetails = useSelector(selectors.selectLeadToProposalLeadDetails);
  const { projectRequirementMet, certificationTier, statusFlags } = currentUser;
  const {
    canRequestProposal,
    proposalNonrequestableReason,
    soloProposalNonrequestableReason,
    auroraNonRequestableReason,
  } = projectDetails;
  const requestedProduct = useSelector(selectors.selectLeadToProposalRequestedProduct);
  const products = productsForHomeownerInterest(leadDetails?.homeownerInterest);
  const isMultiProduct = products.length > 1;
  const firstRequestableProduct = products.find((candidate) => canSendVision(projectDetails, candidate)[0]);
  const product: ProjectProductT | undefined = requestedProduct ?? firstRequestableProduct ?? products[0];
  const [canSendVisionProposal, whyNot] = canSendVision(projectDetails, product);
  const proposalType = useSelector(selectors.selectLeadToProposalDrawerProposalType);
  const isLeadToProposalDrawerOpen = useSelector(selectors.selectLeadToProposalDrawerVisible);
  const { isEnterprise, isAdmin } = useRouteMatcher();
  const isEnterpriseUser = !!currentUser.enterpriseId;
  const currentUserIsAssignedPartner = currentUser.id === assignedPartner?.id;
  const { CAN_REQUEST_VISION_SELF_DESIGN, HAS_VISION_ACCESS } = usePermissions({
    permissionRequests: [permissionTypes.CAN_REQUEST_VISION_SELF_DESIGN, permissionTypes.HAS_VISION_ACCESS],
  });
  const isUncertifiedEnterpriseUser = isEnterpriseUser && !HAS_VISION_ACCESS;

  const { methods, getSmartFormProps } = useSmartForm<ProposalSchemaT>({
    schema,
    useFormProps: { defaultValues: blankFormData },
  });
  const { watch, handleSubmit, setValue, reset, trigger } = methods;
  const values = watch();
  const isVisionProposalSelected = values.proposalType === "vision";
  const isVisionSelfDesign = values.visionSelfDesign && isVisionProposalSelected;
  const isBatteryOnly = product === "battery";
  // Battery-Only has no array to lay out, so it takes the same no-design-queue path as a
  // self-designed proposal: no designer, no design tool, no turnaround, no design notes.
  const skipsDesign = isBatteryOnly || isVisionSelfDesign;
  const isAuroraRequestInProgress = auroraNonRequestableReason === "aurora_in_progress";
  const isDesignToolSelectable = isVisionProposalSelected && !skipsDesign && !isAuroraRequestInProgress;

  useEffectOnce(() => {
    checkForReqdSiteInfo();
  });

  useEffect(() => {
    if (isMultiProduct && !requestedProduct && product) {
      dispatch(actions.setLeadToProposalDrawerRequestedProduct({ requestedProduct: product }));
    }
  }, [isMultiProduct, requestedProduct, product, dispatch]);

  const selectProduct = (selected: string) => {
    dispatch(actions.setLeadToProposalDrawerRequestedProduct({ requestedProduct: selected }));
  };

  useEffect(() => {
    setValue("proposalType", proposalType);
  }, [proposalType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isBatteryOnly && values.proposalType !== "vision") {
      setValue("proposalType", "vision");
    }
  }, [isBatteryOnly, values.proposalType, setValue]);

  useEffect(() => {
    if (!isLeadToProposalDrawerOpen) reset();
  }, [isLeadToProposalDrawerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setValue("skipsDesign", skipsDesign);
  }, [skipsDesign, setValue]);

  useEffect(() => {
    trigger(["proposalType", "visionSelfDesign", "requestedTurnaround"]).then((validForm) => {
      setIsProposalFormValid(validForm);
    });
  }, [
    values.proposalType,
    values.requestedTurnaround,
    values.visionSelfDesign,
    values.skipsDesign,
    trigger,
    setIsProposalFormValid,
  ]);

  useEffect(() => {
    setIsVisionSelfDesign(values.visionSelfDesign && isVisionProposalSelected);
  }, [values.visionSelfDesign, isVisionProposalSelected, setIsVisionSelfDesign]);

  useEffect(() => {
    if (!isDesignToolSelectable) setValue("designTool", "vision");
  }, [isDesignToolSelectable, setValue]);

  useEffect(() => {
    const errorMessages = [projectMessages.ERROR_REQUESTING_PROPOSAL, adminMessages.ERROR_REQUESTING_ADMIN_PROPOSAL];

    if (errorMessages.includes(latestMessage.message)) {
      dispatch(actions.setAlertMessageVisible({ message: `Error: ${latestMessage.message}`, severity: "error" }));
    }
  }, [latestMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const successMessages = [
      projectMessages.REQUEST_VISION_PROPOSAL_SUCCESS,
      adminMessages.REQUEST_ADMIN_VISION_PROPOSAL_SUCCESS,
    ];
    if (successMessages.includes(latestMessage.message)) {
      setIsVisionRequestSubmitted(true);
    }
  }, [latestMessage, setIsVisionRequestSubmitted]);

  useEffect(() => {
    if (proposalNonrequestableReason === "already_requested" && whyNot === "already_requested") {
      dispatch(actions.setLeadToProposalDrawerProposalRequested());
    }
  }, [canSendVisionProposal, canRequestProposal, proposalNonrequestableReason, whyNot]); // eslint-disable-line react-hooks/exhaustive-deps

  const submittingAlertMessage = () => {
    dispatch(
      actions.setAlertMessageVisible({
        message: "Submitting your request. Please wait—this may take a few seconds. . .",
        severity: "info",
        duration: 75000,
      }),
    );
  };

  const handleSoloSubmit = () => {
    const soloTurnaround = { 1: "immediate", 24: "nextDay" };
    dispatch(
      actions.requestProposal({
        id: projectId,
        realTime: soloTurnaround[values.requestedTurnaround],
        proposalNotes: values.proposalNotes,
        enterprise: isEnterprise,
        admin: isAdmin,
        proposalType: values.proposalType,
      }),
    );
  };

  const userCanRequestProposal = () => {
    const SALES_PRO_TIER = 2;

    if (!projectRequirementMet) {
      return false;
    }

    return !(statusFlags.isSetter && certificationTier < SALES_PRO_TIER);
  };

  const handleAuroraSubmit = () => {
    const requestData = {
      providerLeadId: projectId,
      proposalNotes: values.proposalNotes,
      fundingProductId: auroraFinanceOptions?.financeProducts?.[0]?.id,
      fundingCompanySlug: values.proposalType,
      requestedTurnaround: parseInt(values.requestedTurnaround, 10),
    };

    dispatch(actions.createFundingProposal(requestData));
  };

  const visionRequestType = (): VisionRequestTypeT => {
    if (skipsDesign) return "self-request";

    const visionTurnaround = { 1: "realtime", 24: "over-night" };
    return visionTurnaround[values.requestedTurnaround];
  };

  const handleVisionSubmit = () => {
    const proposalNotes = skipsDesign ? "" : values.proposalNotes;
    const designTool: DesignToolT = isDesignToolSelectable && values.designTool === "aurora" ? "aurora" : "vision";
    const requestData = {
      id: projectId,
      product,
      enterprise: isEnterprise,
      requestType: visionRequestType(),
      proposalNotes,
      ...(designTool === "aurora" && {
        designTool,
        requestedTurnaround: parseInt(values.requestedTurnaround, 10),
      }),
    };

    if (isAdmin) {
      dispatch(actions.requestAdminVisionProposal(requestData));
    } else {
      dispatch(actions.requestVisionProposal(requestData));
    }
    dispatch(
      actions.setLeadToProposalDrawerRequestDetails({
        requestType: visionRequestType(),
        proposalNotes,
        designTool,
      }),
    );
    setSubmittedDesignTool(designTool);
    setIsVisionRequestSubmitted(true);
  };

  const onSubmit = (): void => {
    submittingAlertMessage();
    if (values.proposalType === "solo") {
      handleSoloSubmit();
    } else if (values.proposalType === "aurora") {
      handleAuroraSubmit();
    } else if (isVisionProposalSelected) {
      handleVisionSubmit();
    }
  };

  const canRequestSolo = (): boolean => {
    return isNull(soloProposalNonrequestableReason);
  };

  const handleSiteInfoRedirect = () => {
    setCurrentTab(L2pTabs.SiteInfo);
  };

  if (!userCanRequestProposal() && !currentUserIsAssignedPartner && assignedPartner) {
    return <CannotRequestProposal assignedPartner={assignedPartner} rsmRequirementsPassed={rsmRequirementsPassed} />;
  }

  return (
    <SmartForm {...getSmartFormProps({})}>
      {isReqdSiteInfoMissing && <MissingSiteInfoBanner handleSiteInfoRedirect={handleSiteInfoRedirect} />}
      <Grid container spacing={1}>
        {isMultiProduct && (
          <>
            <Grid item xs={12}>
              <S.StyledSectionLabel>Product</S.StyledSectionLabel>
            </Grid>
            <Grid item xs={12} sx={{ marginBottom: "20px" }}>
              <SegmentedControl
                options={proposalProductOptions}
                value={product ?? ""}
                onChange={selectProduct}
                aria-label="Proposal product"
                data-test-id="proposal-product"
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <S.StyledSectionLabel>Proposal Type</S.StyledSectionLabel>
        </Grid>
        <Grid item xs={12} sm={6} sx={{ marginBottom: "20px" }}>
          <SmartFormDropdown
            dropdownOptions={[
              {
                label: "Vision",
                value: "vision",
                disabled: !canSendVisionProposal || isUncertifiedEnterpriseUser,
                ...(isUncertifiedEnterpriseUser && { tooltip: "Requires Enterprise Certification" }),
              },
              // Solo and Aurora are solar-only routes: both design a PV array and Aurora opens
              // a funding proposal with a designer and a turnaround. A Battery-Only project
              // has nothing for either to design, so Vision is its only path.
              {
                label: "Solo",
                value: "solo",
                disabled: !canRequestSolo() || isUncertifiedEnterpriseUser || isBatteryOnly,
                hidden: !canRequestSolo() || isBatteryOnly,
                ...(isUncertifiedEnterpriseUser && { tooltip: "Requires Enterprise Certification" }),
              },
              {
                label: "Aurora",
                value: "aurora",
                disabled: !!auroraNonRequestableReason || isUncertifiedEnterpriseUser || isBatteryOnly,
                hidden: !!auroraNonRequestableReason || isBatteryOnly,
                ...(isUncertifiedEnterpriseUser && { tooltip: "Requires Enterprise Certification" }),
              },
            ]}
            name="proposalType"
            mainLabel="Select Proposal Tool*"
            mainLabelStyle={{ marginTop: "10px" }}
            data-test-id="proposal-type"
            disabled={!rsmRequirementsPassed || isReqdSiteInfoMissing}
          />
        </Grid>
        {isVisionProposalSelected && CAN_REQUEST_VISION_SELF_DESIGN && !isBatteryOnly && (
          <>
            <Grid item xs={12}>
              <S.StyledSectionLabel>Design Method</S.StyledSectionLabel>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ marginBottom: "20px" }}>
              <SmartFormDropdown
                dropdownOptions={[
                  { label: "Powur Proposal Design Team", value: false },
                  { label: "Design Proposal Myself", value: true },
                ]}
                name="visionSelfDesign"
                mainLabel="Select who will be performing the design*"
                mainLabelStyle={{ marginTop: "10px" }}
                data-test-id="design-method"
                disabled={isReqdSiteInfoMissing}
              />
            </Grid>
          </>
        )}
        {isDesignToolSelectable && (
          <>
            <Grid item xs={12}>
              <S.StyledSectionLabel>Design Tool</S.StyledSectionLabel>
            </Grid>
            <Grid item xs={12} sx={{ marginBottom: "20px" }} data-test-id="design-tool">
              <DesignToolSelector
                name="designTool"
                auroraNonRequestableReason={auroraNonRequestableReason}
                disabled={!rsmRequirementsPassed || isReqdSiteInfoMissing || isUncertifiedEnterpriseUser}
              />
            </Grid>
          </>
        )}
        {!skipsDesign && (
          <>
            <Grid item xs={12}>
              <S.StyledSectionLabel>Requested Turnaround Time</S.StyledSectionLabel>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ marginBottom: "20px" }}>
              <SmartFormDropdown
                dropdownOptions={[
                  { label: "Expedited", value: "1" },
                  { label: "Standard", value: "24" },
                ]}
                name="requestedTurnaround"
                placeholder="Select One"
                mainLabel="Time Needed*"
                mainLabelStyle={{ marginTop: "10px" }}
                data-test-id="turnaround"
                disabled={!rsmRequirementsPassed || isReqdSiteInfoMissing || isUncertifiedEnterpriseUser}
                {...(isUncertifiedEnterpriseUser && { fieldHoverTooltipText: "Requires Enterprise Certification" })}
              />
            </Grid>
            <Grid item xs={12}>
              <S.StyledSectionLabel>Proposal Request Notes</S.StyledSectionLabel>
            </Grid>
            <Grid item xs={12} sm={9}>
              <SmartFormTextarea
                name="proposalNotes"
                placeholder="Please share any design goals for this proposal including equipment or aesthetic preferences."
                height={145}
                disabled={!rsmRequirementsPassed || isReqdSiteInfoMissing || isUncertifiedEnterpriseUser}
                {...(isUncertifiedEnterpriseUser && { fieldHoverTooltipText: "Requires Enterprise Certification" })}
              />
            </Grid>
          </>
        )}
        {isVisionProposalSelected && (
          <Grid item xs={12} sx={{ marginBottom: "20px" }}>
            {isBatteryOnly && <BatteryOnlyNotes />}
            {!isBatteryOnly && (isVisionSelfDesign ? <SelfDesignNotes /> : <DesignTeamNotes />)}
          </Grid>
        )}
      </Grid>
      <button ref={reference} style={{ display: "none" }} type="button" onClick={handleSubmit(onSubmit)} />
    </SmartForm>
  );
};

export default ProposalRequestForm;
